import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Table } from "../ui/Table/Table";
import { Input } from "../ui/Input/Input";
import { LoadingSpinner } from "../ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../ui/ErrorFallback/ErrorFallback";
import { Modal } from "../ui/Modal/Modal";
import { useWarehouseLabel } from "../../hooks/use-warehouses";
import { formatDate, formatQuantity } from "../../lib/date";
import { useStockCategoryView, type StockCategoryViewRow } from "../../hooks/use-stock-items";
import styles from "./StockCategoryView.module.css";

type Row = StockCategoryViewRow & { isTotal?: boolean };

interface Props {
	category: string;
	brand: string;
	onBrandChange: (brand: string) => void;
	onOpenOrders: (item: { id: string; name: string }) => void;
	onIncoming: (item: { id: string; name: string }) => void;
	onOpenPurchases: (item: { id: string; name: string }) => void;
}

function sumRows(rows: StockCategoryViewRow[], label: string): Row {
	const incoming: Record<string, number> = {};
	for (const r of rows) {
		for (const [date, qty] of Object.entries(r.incoming))
			incoming[date] = (incoming[date] ?? 0) + qty;
	}
	const sum = (
		key:
			| "eregli"
			| "usak"
			| "other"
			| "total"
			| "committed"
			| "committedViaSet"
			| "incomingTotal"
			| "openPurchase",
	) => rows.reduce((acc, r) => acc + r[key], 0);
	return {
		id: null,
		itemCode: "",
		itemName: label,
		brand: "",
		eregli: sum("eregli"),
		usak: sum("usak"),
		other: sum("other"),
		otherWarehouses: [],
		total: sum("total"),
		committed: sum("committed"),
		committedViaSet: sum("committedViaSet"),
		incoming,
		incomingTotal: sum("incomingTotal"),
		openPurchase: sum("openPurchase"),
		isTotal: true,
	};
}

export function StockCategoryView({
	category,
	brand,
	onBrandChange,
	onOpenOrders,
	onIncoming,
	onOpenPurchases,
}: Props) {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [warehousesRow, setWarehousesRow] = useState<Row | null>(null);
	const warehouseLabel = useWarehouseLabel();
	const [includeEmpty, setIncludeEmpty] = useState(false);
	const { data, isLoading, isError, refetch } = useStockCategoryView(category, includeEmpty);

	const allRows = useMemo(() => data?.rows ?? [], [data?.rows]);
	const brands = useMemo(() => {
		const counts = new Map<string, number>();
		for (const r of allRows) counts.set(r.brand, (counts.get(r.brand) ?? 0) + 1);
		return [...counts.entries()];
	}, [allRows]);

	const rows = useMemo((): Row[] => {
		const term = search.trim().toLocaleUpperCase("tr");
		const filtered = allRows.filter(
			(r) =>
				(!brand || r.brand === brand) &&
				(!term ||
					r.itemCode.toLocaleUpperCase("tr").includes(term) ||
					r.itemName.toLocaleUpperCase("tr").includes(term)),
		);
		return filtered.length > 0 ? [...filtered, sumRows(filtered, t("total"))] : filtered;
	}, [allRows, brand, search, t]);

	const dates = useMemo(
		() => (data?.dates ?? []).filter((d) => rows.some((r) => !r.isTotal && r.incoming[d])),
		[data?.dates, rows],
	);

	const quantity = (row: Row, value: number) => {
		const text = formatQuantity(value);
		return row.isTotal ? <strong>{text}</strong> : text;
	};

	const clickable = (
		row: Row,
		value: number,
		onClick: (item: { id: string; name: string }) => void,
	) =>
		row.id && value !== 0 ? (
			<button
				type="button"
				className={styles.cellButton}
				onClick={() => onClick({ id: row.id as string, name: row.itemName })}
			>
				{formatQuantity(value)}
			</button>
		) : (
			quantity(row, value)
		);

	const signed = (row: Row, value: number) => (
		<span className={value < 0 ? styles.negative : undefined}>{quantity(row, value)}</span>
	);

	return (
		<div className={styles.view}>
			<div className={styles.brandTabs}>
				<button
					type="button"
					className={[styles.brandTab, !brand && styles.brandTabActive].filter(Boolean).join(" ")}
					onClick={() => onBrandChange("")}
				>
					{t("all")} ({allRows.length})
				</button>
				{brands.map(([name, count]) => (
					<button
						key={name}
						type="button"
						className={[styles.brandTab, brand === name && styles.brandTabActive]
							.filter(Boolean)
							.join(" ")}
						onClick={() => onBrandChange(name)}
					>
						{name} ({count})
					</button>
				))}
			</div>

			<div className={styles.toolbar}>
				<Input
					className={styles.search}
					placeholder={t("search")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<label className={styles.checkbox}>
					<input
						type="checkbox"
						checked={includeEmpty}
						onChange={(e) => setIncludeEmpty(e.target.checked)}
					/>
					{t("stockShowEmpty")}
				</label>
				{data && !data.committedAvailable && (
					<span className={styles.warning}>{t("stockCommittedUnavailable")}</span>
				)}
			</div>

			{isLoading && <LoadingSpinner />}
			{isError && <ErrorFallback message={t("sapConnectionError")} onRetry={refetch} />}
			{!isLoading && !isError && (
				<Table<Row>
					className={styles.table}
					columns={[
						{
							key: "itemCode",
							header: t("code"),
							render: (r) => r.itemCode,
						},
						{
							key: "itemName",
							header: t("name"),
							render: (r) => (r.isTotal ? <strong>{r.itemName}</strong> : r.itemName),
						},
						{ key: "eregli", header: t("stockEregli"), render: (r) => quantity(r, r.eregli) },
						{ key: "usak", header: t("stockUsak"), render: (r) => quantity(r, r.usak) },
						{
							key: "other",
							header: t("stockOtherWarehouses"),
							render: (r) =>
								r.otherWarehouses.length > 0 ? (
									<button
										type="button"
										className={styles.cellButton}
										onClick={() => setWarehousesRow(r)}
									>
										{formatQuantity(r.other)}
									</button>
								) : (
									quantity(r, r.other)
								),
						},
						...dates.map((date) => ({
							key: `incoming-${date}`,
							header: formatDate(date),
							render: (r: Row) =>
								r.incoming[date]
									? clickable(r, r.incoming[date], onIncoming)
									: r.isTotal
										? quantity(r, 0)
										: "",
						})),
						// {
						// 	key: "openPurchase",
						// 	header: t("stockOpenPurchase"),
						// 	render: (r) => clickable(r, r.openPurchase, onOpenPurchases),
						// },
						{
							key: "committedDirect",
							header: t("stockOrderedDirect"),
							render: (r) => clickable(r, r.committed - r.committedViaSet, onOpenOrders),
						},
						{
							key: "committedViaSet",
							header: t("stockOrderedViaSet"),
							render: (r) => clickable(r, r.committedViaSet, onOpenOrders),
						},
						{
							key: "remaining",
							header: t("remaining"),
							render: (r) => signed(r, r.total - r.committed),
						},
						{
							key: "remainingWithIncoming",
							header: t("stockRemainingWithIncoming"),
							render: (r) => signed(r, r.total - r.committed + r.incomingTotal /* + r.openPurchase */),
						},
					]}
					data={rows}
					keyExtractor={(r) => (r.isTotal ? "__total" : r.itemCode)}
				/>
			)}

			{warehousesRow && (
				<Modal
					isOpen
					onClose={() => setWarehousesRow(null)}
					title={`${t("stockOtherWarehouses")}: ${warehousesRow.itemName}`}
					maxWidth="min(95vw, 600px)"
				>
					<table className={styles.detailTable}>
						<thead>
							<tr>
								<th>{t("warehouse")}</th>
								<th>{t("quantity")}</th>
							</tr>
						</thead>
						<tbody>
							{warehousesRow.otherWarehouses.map((w) => (
								<tr key={w.whsCode}>
									<td>{warehouseLabel(w.whsCode)}</td>
									<td>{formatQuantity(w.onHand)}</td>
								</tr>
							))}
							<tr>
								<td>
									<strong>{t("total")}</strong>
								</td>
								<td>
									<strong>{formatQuantity(warehousesRow.other)}</strong>
								</td>
							</tr>
						</tbody>
					</table>
				</Modal>
			)}
		</div>
	);
}
